// Input masks + validators shared by the admin New Client interview and the
// client "Confirm your details" gate. Masks run as the user types; validators
// return an error string (or "" when valid).

const digits = (v) => String(v || "").replace(/\D/g, "");

export const maskSSN = (v) => digits(v).slice(0, 4);

export const validateSSN = (v, { required = true } = {}) => {
  const d = digits(v).slice(0, 4);
  if (!d) return required ? "Last 4 of SSN / ITIN is required." : "";
  if (d.length !== 4) return "Enter the last 4 digits of the SSN / ITIN.";
  return "";
};

export const maskDOB = (v) => {
  const d = digits(v).slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

export const validateDOB = (v, { required = true, label = "Date of birth" } = {}) => {
  const raw = String(v || "").trim();
  if (!raw) return required ? `${label} is required.` : "";
  const m = raw.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return `${label} must be MM/DD/YYYY.`;
  const [, mm, dd, yyyy] = m.map(Number);
  const date = new Date(yyyy, mm - 1, dd);
  if (date.getFullYear() !== yyyy || date.getMonth() !== mm - 1 || date.getDate() !== dd) {
    return `${label} isn't a real date.`;
  }
  const today = new Date();
  if (date > today) return `${label} can't be in the future.`;
  let age = today.getFullYear() - yyyy;
  const beforeBirthday = today.getMonth() < mm - 1 || (today.getMonth() === mm - 1 && today.getDate() < dd);
  if (beforeBirthday) age -= 1;
  if (age > 120) return `${label} looks wrong — that would be over 120 years old.`;
  return "";
};

export const maskPhone = (v) => {
  const d = digits(v).replace(/^1(?=\d{10})/, "").slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
};

export const validatePhone = (v, { required = true } = {}) => {
  const d = digits(v).replace(/^1(?=\d{10})/, "");
  if (!d) return required ? "Phone number is required." : "";
  if (d.length !== 10) return "Phone must be a 10-digit US number, e.g. (603) 555-0100.";
  if (/^[01]/.test(d)) return "US area codes don't start with 0 or 1.";
  return "";
};

export const STATE_CODES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY",
  "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH",
  "OK", "OR", "PA", "PR", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY",
];

export const validateAddress = (v, { required = true } = {}) => {
  const raw = String(v || "").trim();
  if (!raw) return required ? "Mailing address is required." : "";
  if (!/\d/.test(raw)) return "Include the street number.";
  if (!/\b\d{5}(-\d{4})?\b/.test(raw)) return "Include a ZIP code (12345 or 12345-6789).";
  const hasState = STATE_CODES.some((s) => new RegExp(`\\b${s}\\b`, "i").test(raw));
  if (!hasState) return "Include the 2-letter state, e.g. NH.";
  return "";
};
